import Cookies from "js-cookie";
import React, {useContext, useEffect, useState} from "react";
import {EnumContext} from "../enumContext";
import {BASE_URL, routes, useGetDataList} from "../../routes/api";
import {
    Alert,
    Autocomplete,
    Backdrop,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Collapse,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    Tabs,
    Tab
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import {SelectOnlyDatePicker} from "../../util/util";
import {useLocation, useNavigate} from "react-router-dom";
import Box from "@mui/material/Box";

export default function DownloadPanel() {
    const token = Cookies.get('token');
    const permissions = Cookies.get('permissions') ?
        JSON.parse(Cookies.get('permissions')).unit :
        {create: false, edit: false, delete: false, view: false};
    const webAdmin = Cookies.get('webAdmin') === 'T';
    const {enums} = useContext(EnumContext);
    const recordRoute = routes.record;
    const alertRoute = routes.alert;

    const navigate = useNavigate();
    const location = useLocation();


    const [route, setRoute] = useState(recordRoute);
    const [open, setOpen] = useState({unit_ids: false, camera_ids: false, start_date: false, end_date: false});
    const [searchParams, setSearchParams] = useState({});
    const [searchBy, setSearchBy] = useState('');
    const [dateError, setDateError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [noData, setNoData] = useState(false);
    const [error, setError] = useState(false);
    const [SN, setSN] = useState({unit_ids: [], camera_ids: []});
    const [message, setMessage] = useState('');

    const [tabValue, setTabValue] = useState('record');

    const {data: cameraData, loading: cameraLoading, handleCall: cameraCall} = useGetDataList(token, routes.camera);
    const {data: unitData, loading: unitLoading, handleCall: unitCall} = useGetDataList(token, routes.unit);

    const fetchData = async () => {
        try {
            await cameraCall({params: {basic: 'T'}});
            await unitCall({params: {basic: 'T'}});
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    }

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        setDateError(Boolean(searchParams.start_date && searchParams.end_date && searchParams.end_date < searchParams.start_date));
    }, [searchParams.start_date, searchParams.end_date]);

    const handleTypeChange = (event) => {
        setSearchBy(event.target.value);
    }

    const handleTabChange = (event, newTabValue) => {
        setTabValue(newTabValue);
        setRoute(newTabValue === 'record' ? recordRoute : alertRoute);
    };

    const handleChange = (newValue, key) => {
        setSearchParams({
            ...searchParams,
            [key]: newValue.map((item) => item[0])
        });
        setSN({...SN, [key]: newValue.map((item) => item[1])})
    }

    const handleDateChange = (key, value) => {
        if (!value) {
            const {[key]: _, ...tempParams} = searchParams;
            setSearchParams(tempParams);
        } else {
            setSearchParams({
                ...searchParams,
                [key]: value.format('YYYY-MM-DD')
            })
        }
    }

    const handleSubmit = async () => {
        const params = {...searchParams};
        if (searchBy === 'unit_ids') {
            delete params.camera_ids;
        } else if (searchBy === 'camera_ids') {
            delete params.unit_ids;
        }

        let objectUrl = '';

        setLoading(true);
        const response = await fetch(`${BASE_URL}${route}/csvexport/`, {
            headers: {
                'Authorization': `Token ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(params),
            method: 'POST',
        });
        if (response.ok) {
            setNoData(false);
            setLoading(false);
            const blob = await response.blob();
            objectUrl = URL.createObjectURL(blob);
        } else if (response.status === 401) {
            // Redirect to login page
            Cookies.remove('token');
            Cookies.remove('permissions');
            Cookies.remove('username');
            navigate('/login', {state: {from: location}});
        } else if (response.status === 410 || response.status === 204) {
            setNoData(true);
            setLoading(false);
            return;
        } else {
            setError(true);
            setMessage(await response.json());
            setLoading(false);
            return;
        }

        const link = document.createElement('a');
        link.href = objectUrl;
        const SNs = SN[searchBy].length < 3 ? SN[searchBy].join('-') : `${SN[searchBy].slice(0, 3).join('-')}…`;
        link.download = `${SNs}_${params.start_date || 'start'}_to_${params.end_date || 'end'}`;
        link.target = '_blank';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setLoading(false);
    };

    const getValue = (options, key) => {
        let value;
        if (!searchParams[key]) {
            value = [];
        } else {
            value = searchParams[key].map(item => options.find(option => option[0] === item)).filter(item => item);
        }
        return value;
    }

    return (
        <Card sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '16px',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            padding: 2,
            paddingBottom: 0
        }}>
            <Backdrop
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.25)',
                    color: '#ccc',
                }}
                open={loading}
            >
                <CircularProgress color="primary"/>
            </Backdrop>
            <CardHeader
                title='Export to CSV'
                subheader='support up to 50,000 rows'
                key='download-header'
                slotProps={{
                    subheader: {variant: 'overline'}
                }}
            />
            <CardContent sx={{
                borderRadius: 2,
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
            }}>
                <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="Records" value='record'/>
                        <Tab label="Alerts" value='alert'/>
                    </Tabs>
                </Box>
                <Stack
                    direction="column"
                    spacing={4}
                    sx={{
                        mt: 3,
                        justifyContent: "flex-start",
                        alignItems: "center",
                    }}
                >
                    <FormControl required error={!searchBy}>
                        <RadioGroup
                            row
                            onChange={handleTypeChange}
                            name="search-by-criteria"
                            value={searchBy}
                        >
                            <FormControlLabel value="unit_ids" control={<Radio/>} label="Search by Unit"/>
                            <FormControlLabel value="camera_ids" control={<Radio/>} label="Search by Camera"/>
                        </RadioGroup>
                    </FormControl>
                    {searchBy === 'unit_ids' &&
                        <Autocomplete
                            id='unit'
                            open={open.unit_ids}
                            onOpen={
                                () => setOpen((states) => ({...states, 'unit_ids': true}))
                            }
                            onClose={
                                () => setOpen((states) => ({...states, 'unit_ids': false}))
                            }
                            label='unit'
                            name='unit'
                            value={getValue(unitData, 'unit_ids')}
                            filterSelectedOptions
                            isOptionEqualToValue={(option, v) => option[0] === v[0]}
                            getOptionLabel={(option) => option[1]}
                            getOptionKey={(option) => option[0]}
                            options={unitData}
                            onChange={(e, newValue) => {
                                handleChange(newValue, 'unit_ids');
                            }}
                            loading={unitLoading}
                            multiple
                            disableCloseOnSelect
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Unit'
                                    required
                                    error={!searchParams.unit_ids}
                                    slotProps={{
                                        input: {
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {unitLoading ? <CircularProgress color="inherit" size={20}/> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }
                                    }}
                                />
                            )}
                            sx={{minWidth: 350}}
                        />
                    }
                    {searchBy === 'camera_ids' &&
                        <Autocomplete
                            id='camera'
                            required
                            open={open.camera}
                            onOpen={
                                () => setOpen((states) => ({...states, 'camera_ids': true}))
                            }
                            onClose={
                                () => setOpen((states) => ({...states, 'camera_ids': false}))
                            }
                            label='camera'
                            name='camera'
                            value={getValue(cameraData, 'camera_ids')}
                            filterSelectedOptions
                            isOptionEqualToValue={(option, v) => option[0] === v[0]}
                            getOptionLabel={(option) => option[1]}
                            getOptionKey={(option) => option[0]}
                            options={cameraData}
                            onChange={(e, newValue) => {
                                handleChange(newValue, 'camera_ids');
                            }}
                            loading={cameraLoading}
                            multiple
                            disableCloseOnSelect
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Camera'
                                    required
                                    error={!searchParams.camera_ids}
                                    slotProps={{
                                        input: {
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {cameraLoading ?
                                                        <CircularProgress color="inherit" size={20}/> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }
                                    }}
                                />
                            )}
                            sx={{minWidth: 350}}
                        />
                    }
                    {searchBy && <LocalizationProvider dateAdapter={AdapterDayjs} key={'date-select'}>
                        <SelectOnlyDatePicker
                            id='start_date'
                            format='YYYY-MM-DD'
                            name='start_date'
                            disableFuture
                            value={searchParams.start_date ? dayjs(searchParams.start_date) : null}
                            onChange={(value) => handleDateChange('start_date', value)}
                            onError={() => setDateError(true)}
                            label='Start Date'
                            slotProps={{
                                textField: {
                                    sx: {minWidth: 350},
                                },
                            }}
                        />
                        <SelectOnlyDatePicker
                            id='end_date'
                            format='YYYY-MM-DD'
                            name='end_date'
                            disableFuture
                            value={searchParams.end_date ? dayjs(searchParams.end_date) : null}
                            onChange={(value) => handleDateChange('end_date', value)}
                            label='End Date'
                            minDate={searchParams.start_date ? dayjs(searchParams.start_date) : dayjs('1900-01-01')}
                            slotProps={{
                                textField: {
                                    sx: {minWidth: 350},
                                },
                            }}
                        />
                    </LocalizationProvider>}
                </Stack>
            </CardContent>
            <Collapse in={noData || error}>
                <Alert severity="error"
                       action={
                           <IconButton
                               aria-label="close"
                               color="inherit"
                               size="small"
                               onClick={() => {
                                   setNoData(false);
                                   setError(false);
                               }}
                           >
                               <CloseIcon fontSize="inherit"/>
                           </IconButton>
                       }
                       sx={{whiteSpace: 'pre-line', mt: 1, mb: 1}}
                >
                    {noData ?
                        `No ${tabValue}s available. Please broaden your search criteria and try again.` :
                        `Error with the search, ${message}`}
                </Alert>
            </Collapse>
            <CardActions sx={{display: 'flex', justifyContent: 'center', mb: 7}}>
                <Button
                    variant='contained'
                    onClick={handleSubmit}
                    disabled={!searchBy || dateError ||
                        (searchBy === 'unit_ids' && (!searchParams.unit_ids || searchParams.unit_ids.length === 0)) ||
                        (searchBy === 'camera_ids' && (!searchParams.camera_ids || searchParams.camera_ids.length === 0))}>
                    Submit
                </Button>
            </CardActions>
        </Card>

    )
}